import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useWeb3 } from '../contexts/Web3Context';
import api, { API_ENDPOINTS } from '../config/api';
import axios from 'axios';
import { 
  Wallet, 
  DollarSign, 
  CreditCard,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownLeft,
  ArrowLeftRight,
  Shield,
  Clock,
  CheckCircle,
  AlertCircle,
  Download,
  Upload,
  Banknote,
  Copy,
  X,
  RefreshCw,
  Send,
  ExternalLink
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import { Contract, formatEther, parseEther, parseUnits, formatUnits } from 'ethers';
import { LOB_TOKEN_ADDRESS, USDC_TOKEN_ADDRESS, BASE_CHAIN_PARAMS, WORKLOOB_NET } from '../config/Constants';
import { formatWalletError, dismissWalletToasts } from '../utils/walletErrors';
import { transferToken, transferNativeEth } from '../config/tokenTransfer';
import ERC20_ABI_JSON from '../abi/ERC20.json';
import ethCoinLogo from '../assets/eth-coin.png';
import usdcCoinLogo from '../assets/usdc.png';
import workloobCoinLogo from '../assets/workloob-coin.png';

const ERC20_ABI = ERC20_ABI_JSON.abi;

async function readTokenDecimals(contract, fallback = 18) {
  try {
    return Number(await contract.decimals());
  } catch {
    return fallback;
  }
}

// ERC20 tokens for the active network (testnet or mainnet)
const SUPPORTED_TOKENS = [
  {
    symbol: 'ETH',
    name: 'Ethereum',
    address: null, // Native token
    decimals: 18,
    logo: ethCoinLogo
  },
  {
    symbol: 'USDC',
    name: 'USD Coin',
    address: USDC_TOKEN_ADDRESS,
    decimals: 6, // common default; if your test token differs, balances still work via UI display
    logo: usdcCoinLogo
  },
  {
    symbol: 'LOB',
    name: 'LOB Token',
    address: LOB_TOKEN_ADDRESS,
    decimals: 18,
    logo: workloobCoinLogo
  },
  // Add more tokens as needed
];

const DEFAULT_TRANSFER_TOKEN = SUPPORTED_TOKENS.find((t) => t.symbol === 'USDC') || SUPPORTED_TOKENS[1];

const WalletPage = () => {
  const { user, loginMethod, refreshUser } = useAuth();
  const { 
    isConnected, 
    walletAddress, 
    walletBalance, 
    provider, 
    signer,
    connectWallet,
    disconnectWallet
  } = useWeb3();
  const queryClient = useQueryClient();
  
  const [activeTab, setActiveTab] = useState('transactions'); // Default to Transaction History
  const [ethPrice, setEthPrice] = useState(null);
  const [selectedTransactionType, setSelectedTransactionType] = useState('');
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [tokenBalances, setTokenBalances] = useState([]);
  const [isLoadingBalances, setIsLoadingBalances] = useState(false);
  const [transactionsPage, setTransactionsPage] = useState(1);

  // Fetch USDC price for USD conversion (treated as ~1:1)
  const { data: ethPriceData } = useQuery(
    'usdcPrice',
    async () => {
      try {
        // Use the same endpoint as Escrowintegration.js
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const response = await axios.get(`${API_URL}/api/v1/price/usdc`);
        return response.data;
      } catch (error) {
        console.error("Failed to get USDC price from backend:", error);
        return null;
      }
    },
    {
      select: (data) => data,
      staleTime: 300000, // Cache for 5 minutes
      refetchInterval: 300000, // Refetch every 5 minutes
      onSuccess: (data) => {
        if (data?.usd) {
          setEthPrice(data.usd);
        }
      }
    }
  );

  // Update ethPrice state when data changes
  useEffect(() => {
    if (ethPriceData?.usd) {
      setEthPrice(ethPriceData.usd);
    }
  }, [ethPriceData]);

  // Fetch escrow details (sum from jobs/gigs with inprogress/completed status)
  const { data: escrowData, isLoading: escrowLoading, refetch: refetchEscrow } = useQuery(
    'escrowDetails',
    () => api.get(API_ENDPOINTS.WALLET.ESCROW_DETAILS),
    {
      select: (data) => data.data,
      enabled: !!user,
      staleTime: 30000, // Cache for 30 seconds
      refetchInterval: 60000, // Auto-refetch every 60 seconds
      refetchOnWindowFocus: true, // Refetch when window regains focus
    }
  );

  // Fetch transactions
  const { data: transactionsData, isLoading: transactionsLoading, refetch: refetchTransactions } = useQuery(
    ['walletTransactions', { type: selectedTransactionType, page: transactionsPage }],
    () => api.get(API_ENDPOINTS.WALLET.TRANSACTIONS, { 
      params: { 
        page: transactionsPage,
        limit: 20,
        ...(selectedTransactionType && { type: selectedTransactionType })
      }
    }),
    {
      select: (data) => data.data,
      enabled: !!user,
      staleTime: 30000, // Cache for 30 seconds
      refetchInterval: 60000, // Auto-refetch every 60 seconds
      refetchOnWindowFocus: true, // Refetch when window regains focus
    }
  );

  // Calculate escrow balance first (needed for totalBalanceUSD calculation)
  const escrowBalance = useMemo(() => {
    if (!escrowData?.totalEscrow) return 0;
    return parseFloat(escrowData.totalEscrow) || 0;
  }, [escrowData]);

  // Available balance should reflect USDC token balance
  const availableBalanceUSDC = useMemo(() => {
    const usdc = tokenBalances.find(t => t.symbol === 'USDC');
    return usdc?.balance ? Number(usdc.balance) : 0;
  }, [tokenBalances]);

  // Convert USDC balance to USD (treated as ~1:1)
  const availableBalanceUSD = useMemo(() => {
    if (!ethPrice || !isConnected) return 0;
    return availableBalanceUSDC * Number(ethPrice);
  }, [availableBalanceUSDC, ethPrice, isConnected]);

  // Total balance in USD (Available + Escrow)
  const totalBalanceUSD = useMemo(() => {
    return availableBalanceUSD + escrowBalance;
  }, [availableBalanceUSD, escrowBalance]);

  // Display wallet balance info for debugging
  useEffect(() => {
    if (isConnected && walletAddress) {
      console.log('WalletPage: Wallet connected', {
        address: walletAddress,
        balanceUSDC: availableBalanceUSDC,
        balanceUSD: availableBalanceUSD,
        usdcPrice: ethPrice,
        isConnected,
        hasProvider: !!provider
      });
    }
  }, [isConnected, walletAddress, availableBalanceUSDC, availableBalanceUSD, ethPrice, provider]);

  const transactions = transactionsData?.transactions || [];
  
  // Reset to page 1 when transaction type filter changes
  useEffect(() => {
    setTransactionsPage(1);
  }, [selectedTransactionType]);
  const escrowDetails = escrowData?.escrowDetails || [];

  // Fetch token balances with auto-refresh
  useEffect(() => {
    const fetchTokenBalances = async () => {
      if (!isConnected || !provider || !walletAddress) {
        setTokenBalances([]);
        return;
      }

      setIsLoadingBalances(true);
      try {
        const balances = await Promise.all(
          SUPPORTED_TOKENS.map(async (token) => {
            try {
              let balance = '0';
              
              if (token.address === null) {
                // Native token (ETH)
                const nativeBalance = await provider.getBalance(walletAddress);
                balance = formatEther(nativeBalance);
              } else {
                // ERC20 token
                const contract = new Contract(token.address, ERC20_ABI, provider);
                const tokenBalance = await contract.balanceOf(walletAddress);
                // Read decimals from-chain to support arbitrary test tokens
                let decimals = token.decimals;
                try {
                  decimals = Number(await contract.decimals());
                } catch (e) {
                  // ignore and fall back to configured decimals
                }
                balance = formatUnits(tokenBalance, decimals);
              }

              return {
                ...token,
                balance: parseFloat(balance),
                formattedBalance: parseFloat(balance).toFixed(6)
              };
            } catch (error) {
              console.error(`Error fetching balance for ${token.symbol}:`, error);
              return {
                ...token,
                balance: 0,
                formattedBalance: '0.000000'
              };
            }
          })
        );

        // Always show ETH, USDC and LOB, even if balance is 0
        // Filter other tokens to only show those with balance > 0
        const tokensWithBalance = balances.filter(token => 
          token.balance > 0 || token.symbol === 'ETH' || token.symbol === 'USDC' || token.symbol === 'LOB'
        );
        setTokenBalances(tokensWithBalance);
      } catch (error) {
        console.error('Error fetching token balances:', error);
        toast.error('Failed to fetch token balances');
      } finally {
        setIsLoadingBalances(false);
      }
    };

    fetchTokenBalances();
    
    // Set up interval to auto-refresh balances every 10 seconds for continuous updates
    const interval = setInterval(() => {
      if (isConnected && provider && walletAddress) {
        fetchTokenBalances();
      }
    }, 10000); // Fetch every 10 seconds for real-time updates

    return () => clearInterval(interval);
  }, [isConnected, provider, walletAddress]);

  // Fetch on-chain transactions and save to backend
  useEffect(() => {
    const fetchOnChainTransactions = async () => {
      if (!isConnected || !provider || !walletAddress || !user) return;

      try {
        // Get recent transactions from the blockchain
        // This is a simplified version - in production, you'd use a service like Alchemy/Infura
        // or scan the blockchain for transactions
        
        // For now, we'll just check if we need to save any pending transactions
        // In a real implementation, you'd scan the blockchain for transactions
        
      } catch (error) {
        console.error('Error fetching on-chain transactions:', error);
      }
    };

    if (isConnected && walletAddress) {
      fetchOnChainTransactions();
    }
  }, [isConnected, provider, walletAddress, user]);

  const handleDeposit = async () => {
    if (!isConnected) {
      const result = await connectWallet();
      if (!result || !result.address) {
        toast.error('Please connect your wallet first');
        return;
      }
    }
    setShowDepositModal(true);
  };

  const handleSwap = async () => {
    if (!isConnected) {
      const result = await connectWallet();
      if (!result || !result.address) {
        toast.error('Please connect your wallet first');
        return;
      }
    }
    setShowSwapModal(true);
  };

  const handleTransfer = async () => {
    if (!isConnected) {
      const result = await connectWallet();
      if (!result || !result.address) {
        toast.error('Please connect your wallet first');
        return;
      }
    }
    setShowTransferModal(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const transactionTypes = [
    { value: '', label: 'All Transactions' },
    { value: 'deposit', label: 'Deposits' },
    { value: 'withdrawal', label: 'Withdrawals' },
    { value: 'transfer', label: 'Transfers' },
    { value: 'swap', label: 'Swaps' },
    { value: 'payment', label: 'Payments' },
    { value: 'escrow', label: 'Escrow' },
    { value: 'refund', label: 'Refunds' },
    { value: 'bonus', label: 'Bonuses' },
    { value: 'referral', label: 'Referrals' }
  ];

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'deposit':
        return <ArrowDownLeft className="w-4 h-4 text-green-500" />;
      case 'withdrawal':
        return <ArrowUpRight className="w-4 h-4 text-red-500" />;
      case 'transfer':
        return <Send className="w-4 h-4 text-blue-500" />;
      case 'swap':
        return <ArrowLeftRight className="w-4 h-4 text-purple-500" />;
      case 'payment':
        return <CreditCard className="w-4 h-4 text-blue-500" />;
      case 'escrow':
        return <Shield className="w-4 h-4 text-yellow-500" />;
      case 'refund':
        return <AlertCircle className="w-4 h-4 text-orange-500" />;
      case 'bonus':
        return <Banknote className="w-4 h-4 text-purple-500" />;
      case 'referral':
        return <TrendingUp className="w-4 h-4 text-indigo-500" />;
      default:
        return <DollarSign className="w-4 h-4 text-gray-500" />;
    }
  };

  const getTransactionStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'cancelled':
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  // Determine if a transaction is a credit (positive) or debit (negative) for the current user
  const isTransactionCredit = (transaction) => {
    const currentUserId = user?._id || user?.id;
    if (!currentUserId) return true; // Default to credit if no user
    
    // Check fromUser and toUser fields first (most reliable)
    const fromUserId = transaction.fromUser?._id || transaction.fromUser;
    const toUserId = transaction.toUser?._id || transaction.toUser;
    const currentUserIdStr = currentUserId.toString();
    
    // Normalize IDs to strings for comparison
    const fromUserIdStr = fromUserId ? fromUserId.toString() : null;
    const toUserIdStr = toUserId ? toUserId.toString() : null;
    
    // If current user is the recipient (toUser), it's a credit
    if (toUserIdStr && toUserIdStr === currentUserIdStr) {
      return true;
    }
    
    // If current user is the sender (fromUser), it's a debit
    if (fromUserIdStr && fromUserIdStr === currentUserIdStr) {
      return false;
    }
    
    // Fallback: determine based on transaction type
    switch (transaction.type) {
      case 'deposit':
      case 'refund':
      case 'bonus':
      case 'referral':
      case 'escrow_release':
      case 'escrow_completion':
      case 'escrow_confirm':
      case 'job_payment':
      case 'gig_payment':
        return true; // These are always credits
      
      case 'withdrawal':
        return false; // Always a debit
      
      case 'escrow_deposit':
        // Escrow deposit: client deposits (debit for client), but this is just moving to escrow
        // If we're the fromUser (client), it's a debit
        if (fromUserIdStr && fromUserIdStr === currentUserIdStr) {
          return false; // Client depositing = debit
        }
        // Talent doesn't receive anything at deposit time, so this shouldn't show for talent
        return false; // Default to debit
      
      case 'escrow_disburse':
        // Disbursement: debit for client (fromUser), credit for talent (toUser)
        if (fromUserIdStr && fromUserIdStr === currentUserIdStr) {
          return false; // Client disbursing = debit
        }
        if (toUserIdStr && toUserIdStr === currentUserIdStr) {
          return true; // Talent receiving = credit
        }
        return false; // Default to debit if unclear
      
      case 'transfer':
        // Transfer: debit for sender, credit for receiver
        if (toUserIdStr && toUserIdStr === currentUserIdStr) {
          return true; // We're receiving
        }
        if (fromUserIdStr && fromUserIdStr === currentUserIdStr) {
          return false; // We're sending
        }
        return false; // Default to debit if unclear
      
      default:
        // If direction is set and we couldn't determine from fromUser/toUser, use it as fallback
        if (transaction.direction) {
          return transaction.direction === 'credit';
        }
        // Default: if we're not the sender, assume credit
        return !fromUserIdStr || fromUserIdStr !== currentUserIdStr;
    }
  };

  const formatAmount = (amount, currency = 'ETH', isCredit = true) => {
    const formatted = Math.abs(amount).toFixed(6);
    const sign = isCredit ? '+' : '-';
    return `${sign}${formatted} ${currency}`;
  };

  const formatTimestamp = (timestamp) => {
    return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-bold text-heading mb-2">
          Wallet Management
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Manage your funds, track transactions, and monitor escrow balances
        </p>
      </div>

      {/* Wallet Connection Section - Show if user logged in with email */}
      {loginMethod === 'email' && (
        <div className="card p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Web3 Wallet Connection
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {isConnected 
                  ? `Connected to ${walletAddress ? `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}` : 'wallet'}`
                  : user?.connectedWalletAddress
                  ? `Previously connected: ${user.connectedWalletAddress.slice(0, 6)}...${user.connectedWalletAddress.slice(-4)}`
                  : 'Connect your Web3 wallet to perform transactions'}
              </p>
            </div>
            <div>
              {isConnected ? (
                <button
                  onClick={async () => {
                    try {
                      // Disconnect from Web3 context
                      await disconnectWallet();
                      // Disconnect from backend
                      await api.post(API_ENDPOINTS.WALLET.DISCONNECT);
                      // Refresh user data
                      await refreshUser();
                      toast.success('Wallet disconnected successfully');
                    } catch (error) {
                      console.error('Error disconnecting wallet:', error);
                      toast.error('Failed to disconnect wallet. Please try again.');
                    }
                  }}
                  className="btn-secondary"
                >
                  Disconnect Wallet
                </button>
              ) : (
                <button
                  onClick={async () => {
                    try {
                      const result = await connectWallet();
                      if (result && result.address) {
                        // Save connected wallet to backend
                        await api.post(API_ENDPOINTS.WALLET.CONNECT, {
                          walletAddress: result.address
                        });
                        // Refresh user data
                        await refreshUser();
                        toast.success('Wallet connected successfully');
                      }
                    } catch (error) {
                      console.error('Error connecting wallet:', error);
                      const errorMessage = error.response?.data?.error || 'Failed to connect wallet. Please try again.';
                      toast.error(errorMessage);
                      // Disconnect from Web3 if backend save failed
                      await disconnectWallet();
                    }
                  }}
                  className="btn-primary"
                >
                  Connect Wallet
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Wallet Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Available Balance */}
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Available Balance
              </p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {availableBalanceUSDC.toFixed(2)} USDC
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                {isConnected 
                  ? walletAddress 
                    ? `${availableBalanceUSDC.toFixed(2)} USDC • Wallet: ${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}` 
                    : 'Fetching balance...'
                  : 'Connect wallet to view balance'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                {isConnected
                  ? ethPrice
                    ? `~$${availableBalanceUSD.toFixed(2)} USD`
                    : 'USD estimate loading...'
                  : ''}
              </p>
            </div>
            <div className="text-green-500">
              <Wallet className="w-8 h-8" />
            </div>
          </div>
        </div>

        {/* Escrow Balance */}
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Escrow Balance
              </p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                ${escrowBalance.toFixed(2)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                Secured for active projects
              </p>
            </div>
            <div className="text-yellow-500">
              <Shield className="w-8 h-8" />
            </div>
          </div>
        </div>

        {/* Total Balance */}
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Total Balance
              </p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                ${totalBalanceUSD.toFixed(2)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                Available + Escrow
              </p>
            </div>
            <div className="text-blue-500">
              <TrendingUp className="w-8 h-8" />
            </div>
          </div>
        </div>
      </div>

      {/* Token Balances Table - Always show */}
      <div className="card p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Token Balances
          </h3>
          {isConnected && (
            <button
              onClick={() => {
                if (isConnected && provider && walletAddress) {
                  // Force refresh token balances
                  const fetchTokenBalances = async () => {
                    setIsLoadingBalances(true);
                    try {
                      const balances = await Promise.all(
                        SUPPORTED_TOKENS.map(async (token) => {
                          try {
                            let balance = '0';
                            
                            if (token.address === null) {
                              const nativeBalance = await provider.getBalance(walletAddress);
                              balance = formatEther(nativeBalance);
                            } else {
                              const contract = new Contract(token.address, ERC20_ABI, provider);
                              const tokenBalance = await contract.balanceOf(walletAddress);
                              balance = formatUnits(tokenBalance, token.decimals);
                            }

                            return {
                              ...token,
                              balance: parseFloat(balance),
                              formattedBalance: parseFloat(balance).toFixed(6)
                            };
                          } catch (error) {
                            return {
                              ...token,
                              balance: 0,
                              formattedBalance: '0.000000'
                            };
                          }
                        })
                      );
                      // Always show ETH and LOB, even if balance is 0
                      const tokensWithBalance = balances.filter(token => 
                        token.balance > 0 || token.symbol === 'ETH' || token.symbol === 'LOB'
                      );
                      setTokenBalances(tokensWithBalance);
                    } catch (error) {
                      console.error('Error fetching token balances:', error);
                    } finally {
                      setIsLoadingBalances(false);
                    }
                  };
                  fetchTokenBalances();
                }
              }}
              className="btn-outline flex items-center space-x-2 text-sm"
              disabled={isLoadingBalances}
            >
              <RefreshCw className={`w-4 h-4 ${isLoadingBalances ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                  Token
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                  Balance
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                  Symbol
                </th>
              </tr>
            </thead>
            <tbody>
              {!isConnected ? (
                <tr>
                  <td colSpan="3" className="py-8 text-center text-gray-500 dark:text-gray-400">
                    <div className="flex flex-col items-center space-y-2">
                      <Wallet className="w-8 h-8 text-gray-400" />
                      <p>Your balance is empty</p>
                      <p className="text-sm">Connect your wallet to view token balances</p>
                    </div>
                  </td>
                </tr>
              ) : isLoadingBalances ? (
                <tr>
                  <td colSpan="3" className="py-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto"></div>
                  </td>
                </tr>
              ) : tokenBalances.length === 0 ? (
                <tr>
                  <td colSpan="3" className="py-8 text-center text-gray-500 dark:text-gray-400">
                    <div className="flex flex-col items-center space-y-2">
                      <Wallet className="w-8 h-8 text-gray-400" />
                      <p>Your balance is empty</p>
                      <p className="text-sm">No tokens found in your wallet</p>
                    </div>
                  </td>
                </tr>
              ) : (
                tokenBalances.map((token, index) => (
                  <tr key={index} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-3">
                        {token.logo ? (
                          <img 
                            src={token.logo} 
                            alt={token.name}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                            {token.symbol[0]}
                          </div>
                        )}
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {token.name}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {token.formattedBalance}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                        {token.symbol}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Quick Actions
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button 
            onClick={handleDeposit}
            className="btn-primary flex items-center justify-center"
          >
            <Download className="w-4 h-4 mr-2" />
            Deposit Funds
          </button>
          <button 
            onClick={handleTransfer}
            className="btn-secondary flex items-center justify-center"
          >
            <Send className="w-4 h-4 mr-2" />
            Transfer Funds
          </button>
          <button 
            onClick={handleSwap}
            className="btn-outline flex items-center justify-center"
          >
            <ArrowLeftRight className="w-4 h-4 mr-2" />
            Swap
          </button>
          
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6">
        <button
          onClick={() => setActiveTab('transactions')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'transactions'
              ? 'bg-accent text-white'
              : 'text-gray-600 dark:text-gray-400 hover:text-accent'
          }`}
        >
          Transaction History
        </button>
        <button
          onClick={() => setActiveTab('escrow')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'escrow'
              ? 'bg-accent text-white'
              : 'text-gray-600 dark:text-gray-400 hover:text-accent'
          }`}
        >
          Escrow Details
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'transactions' && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Transaction History
            </h3>
            <select
              value={selectedTransactionType}
              onChange={(e) => setSelectedTransactionType(e.target.value)}
              className="input-field w-48"
            >
              {transactionTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                    Transaction
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                    Amount
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                    Status
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                    Date
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                    Type
                  </th>
                  {isConnected && (
                    <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                      On-Chain
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction) => (
                  <tr key={transaction._id} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-3">
                        {getTransactionIcon(transaction.type)}
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">
                            {transaction.description}
                          </p>
                          {transaction.txHash && (
                            <a
                              href={`https://basescan.org/tx/${transaction.txHash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-accent hover:text-accent/80 flex items-center space-x-1"
                            >
                              <span>{transaction.txHash.slice(0, 10)}...</span>
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {(() => {
                        const isCredit = isTransactionCredit(transaction);
                        return (
                          <span className={`font-medium ${
                            isCredit ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                          }`}>
                            {formatAmount(transaction.amount, transaction.currency || 'ETH', isCredit)}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        {getTransactionStatusIcon(transaction.status)}
                        <span className="capitalize">{transaction.status}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                      {formatTimestamp(transaction.createdAt)}
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 capitalize">
                        {transaction.type}
                      </span>
                    </td>
                    {isConnected && (
                      <td className="py-3 px-4">
                        {transaction.isOnChain ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            On-Chain
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                            Off-Chain
                          </span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {transactions.length > 0 && transactionsData && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                className="btn-outline"
                disabled={transactionsPage === 1 || transactionsLoading}
                onClick={() => setTransactionsPage((prev) => Math.max(prev - 1, 1))}
              >
                Previous
              </button>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Page {transactionsData.currentPage || transactionsPage} of {transactionsData.totalPages || 1}
                {transactionsData.total && (
                  <span className="ml-2">({transactionsData.total} total)</span>
                )}
              </p>
              <button
                className="btn-outline"
                disabled={
                  transactionsPage >= (transactionsData.totalPages || 1) || 
                  transactionsLoading ||
                  transactions.length < 20
                }
                onClick={() => setTransactionsPage((prev) => prev + 1)}
              >
                Next
              </button>
            </div>
          )}

          {transactions.length === 0 && !transactionsLoading && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">💳</div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                No transactions found
              </h4>
              <p className="text-gray-600 dark:text-gray-400">
                Your transaction history will appear here once you start using your wallet.
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'escrow' && (
        <div className="space-y-6">
          {/* Escrow Balance Summary */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Escrow Balance
              </h3>
              <button
                onClick={() => {
                  refetchEscrow();
                  toast.success('Escrow data refreshed');
                }}
                className="btn-outline flex items-center space-x-2"
                disabled={escrowLoading}
              >
                <RefreshCw className={`w-4 h-4 ${escrowLoading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-lg text-white">
                <p className="text-sm opacity-90 mb-2">Total Escrow</p>
                <p className="text-3xl font-bold">
                  ${escrowBalance.toFixed(2)}
                </p>
              </div>
              <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-lg text-white">
                <p className="text-sm opacity-90 mb-2">From Jobs</p>
                <p className="text-3xl font-bold">
                  ${(escrowData?.jobEscrow || 0).toFixed(2)}
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-6 rounded-lg text-white">
                <p className="text-sm opacity-90 mb-2">From Gigs</p>
                <p className="text-3xl font-bold">
                  ${(escrowData?.gigEscrow || 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          {/* Escrow Details Table */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Escrow Details
            </h3>
            
            {escrowLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
              </div>
            ) : escrowDetails.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                        ID
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                        Project Title
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                        Type
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                        Amount
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                        Status
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                        Deposited At
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {escrowDetails.map((escrow) => (
                      <tr key={escrow.id || escrow._id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="py-3 px-4">
                          <div className="flex flex-col space-y-1">
                            <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                              {escrow.type === 'job' ? (escrow.jobId || escrow.id) : (escrow.gigId || escrow.id)}
                            </span>
                            {escrow.chatId && (
                              <span className="font-mono text-xs text-gray-400 dark:text-gray-500">
                                Chat: {escrow.chatId.toString().slice(-8)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100">
                              {escrow.title || 'Untitled Project'}
                            </p>
                            {escrow.userRole && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Your role: {escrow.userRole}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${
                            escrow.type === 'job' || escrow.projectType === 'job'
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                              : 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                          }`}>
                            {escrow.type === 'job' ? 'Job' : escrow.type === 'gig' ? 'Gig' : 'General'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {escrow.currency || 'USD'} {(escrow.amount || 0).toFixed(2)}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${
                            escrow.status === 'completed' 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : escrow.status === 'in-progress'
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                              : escrow.status === 'deposit'
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                          }`}>
                            {escrow.status || 'pending'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400 text-sm">
                          {escrow.depositedAt ? formatTimestamp(escrow.depositedAt) : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🛡️</div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  No active escrow
                </h4>
                <p className="text-gray-600 dark:text-gray-400">
                  You don't have any active escrow deposits at the moment.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Deposit Modal */}
      {showDepositModal && (
        <div className="modal-overlay" onClick={() => setShowDepositModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-heading text-heading dark:text-white">
                Deposit Funds
              </h2>
              <button
                onClick={() => setShowDepositModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              {isConnected && walletAddress ? (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Send your coins to this address:
                  </p>
                  <div className="flex items-center space-x-2 p-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
                    <input
                      type="text"
                      value={walletAddress}
                      readOnly
                      className="flex-1 bg-transparent text-gray-900 dark:text-white font-mono text-sm"
                    />
                    <button
                      onClick={() => copyToClipboard(walletAddress)}
                      className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    Make sure you're sending on the correct network ({BASE_CHAIN_PARAMS.chainName})
                  </p>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Please connect your wallet first
                  </p>
                  <button
                    onClick={async () => {
                      const result = await connectWallet();
                      if (result && result.address) {
                        toast.success('Wallet connected!');
                      }
                    }}
                    className="btn-primary"
                  >
                    Connect Wallet
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Swap Modal */}
      {showSwapModal && <SwapModalComponent isOpen={showSwapModal} onClose={() => setShowSwapModal(false)} />}

      {/* Transfer Modal */}
      {showTransferModal && (
        <TransferModalComponent 
          isOpen={showTransferModal} 
          onClose={() => setShowTransferModal(false)}
          onTransferSuccess={() => {
            // Refresh token balances after successful transfer
            if (isConnected && provider && walletAddress) {
              const fetchTokenBalances = async () => {
                try {
                  const balances = await Promise.all(
                    SUPPORTED_TOKENS.map(async (token) => {
                      try {
                        let balance = '0';
                        
                        if (token.address === null) {
                          const nativeBalance = await provider.getBalance(walletAddress);
                          balance = formatEther(nativeBalance);
                        } else {
                          const contract = new Contract(token.address, ERC20_ABI, provider);
                          const tokenBalance = await contract.balanceOf(walletAddress);
                          balance = formatUnits(tokenBalance, token.decimals);
                        }

                        return {
                          ...token,
                          balance: parseFloat(balance),
                          formattedBalance: parseFloat(balance).toFixed(6)
                        };
                      } catch (error) {
                        return {
                          ...token,
                          balance: 0,
                          formattedBalance: '0.000000'
                        };
                      }
                    })
                  );
                  const tokensWithBalance = balances.filter(token => token.balance > 0);
                  setTokenBalances(tokensWithBalance);
                } catch (error) {
                  console.error('Error refreshing token balances:', error);
                }
              };
              fetchTokenBalances();
            }
            // Refresh transactions
            refetchTransactions();
          }}
        />
      )}
    </div>
  );
};

// Swap Modal Component
const SwapModalComponent = ({ isOpen, onClose }) => {
  const { isConnected, provider, signer, walletAddress, connectWallet } = useWeb3();
  const [fromToken, setFromToken] = useState(SUPPORTED_TOKENS[0]);
  const [toToken, setToToken] = useState(SUPPORTED_TOKENS[1]);
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [isSwapping, setIsSwapping] = useState(false);
  const [fromBalance, setFromBalance] = useState('0');
  const [toBalance, setToBalance] = useState('0');
  const [isLoadingBalances, setIsLoadingBalances] = useState(false);

  // Fetch balances for selected tokens
  useEffect(() => {
    const fetchBalances = async () => {
      if (!isConnected || !provider || !walletAddress) {
        setFromBalance('0');
        setToBalance('0');
        return;
      }

      setIsLoadingBalances(true);
      try {
        // Fetch from token balance
        if (fromToken.address === null) {
          // Native token (ETH)
          const balance = await provider.getBalance(walletAddress);
          setFromBalance(formatEther(balance));
        } else {
          // ERC20 token
          const contract = new Contract(fromToken.address, ERC20_ABI, provider);
          const balance = await contract.balanceOf(walletAddress);
          setFromBalance(formatUnits(balance, fromToken.decimals));
        }

        // Fetch to token balance
        if (toToken.address === null) {
          // Native token (ETH)
          const balance = await provider.getBalance(walletAddress);
          setToBalance(formatEther(balance));
        } else {
          // ERC20 token
          const contract = new Contract(toToken.address, ERC20_ABI, provider);
          const balance = await contract.balanceOf(walletAddress);
          setToBalance(formatUnits(balance, toToken.decimals));
        }
      } catch (error) {
        console.error('Error fetching balances:', error);
        setFromBalance('0');
        setToBalance('0');
      } finally {
        setIsLoadingBalances(false);
      }
    };

    if (isOpen && isConnected) {
      fetchBalances();
    }
  }, [isOpen, isConnected, provider, walletAddress, fromToken, toToken]);

  const handleSwap = async () => {
    if (!isConnected || !signer) {
      toast.error('Please connect your wallet first');
      return;
    }

    if (!fromAmount || parseFloat(fromAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setIsSwapping(true);
    try {
      // In a real implementation, you'd integrate with a DEX like Uniswap, 1inch, etc.
      // For now, this is a placeholder
      toast.error('Swap functionality requires DEX integration. Coming soon!');
      
      // Example swap logic would go here:
      // 1. Approve token if needed
      // 2. Execute swap via DEX router
      // 3. Wait for transaction
      // 4. Save transaction to backend
      
    } catch (error) {
      console.error('Swap error:', error);
      toast.error('Swap failed. Please try again.');
    } finally {
      setIsSwapping(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-heading text-heading dark:text-white">
            Swap Tokens
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {!isConnected ? (
            <div className="text-center py-8">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Please connect your wallet first
              </p>
              <button
                onClick={async () => {
                  const result = await connectWallet();
                  if (result && result.address) {
                    toast.success('Wallet connected!');
                  }
                }}
                className="btn-primary"
              >
                Connect Wallet
              </button>
            </div>
          ) : (
            <>
              {/* From Token */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    From
                  </label>
                  {isLoadingBalances ? (
                    <span className="text-xs text-gray-500">Loading...</span>
                  ) : (
                    <span className="text-xs text-gray-500">
                      Balance: {parseFloat(fromBalance).toFixed(6)} {fromToken.symbol}
                    </span>
                  )}
                </div>
                <div className="flex space-x-2">
                  <select
                    value={fromToken.symbol}
                    onChange={(e) => {
                      const token = SUPPORTED_TOKENS.find(t => t.symbol === e.target.value);
                      if (token) setFromToken(token);
                    }}
                    className="input-field w-32"
                  >
                    {SUPPORTED_TOKENS.map(token => (
                      <option key={token.symbol} value={token.symbol}>
                        {token.symbol}
                      </option>
                    ))}
                  </select>
                  <div className="flex-1 relative">
                    <input
                      type="number"
                      value={fromAmount}
                      onChange={(e) => setFromAmount(e.target.value)}
                      placeholder="0.0"
                      className="input-field w-full pr-16"
                      step="0.000001"
                      min="0"
                      max={fromBalance}
                    />
                    <button
                      onClick={() => setFromAmount(fromBalance)}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-accent hover:text-accent/80 font-medium"
                    >
                      MAX
                    </button>
                  </div>
                </div>
              </div>

              {/* Swap Button */}
              <div className="flex justify-center">
                <button
                  onClick={() => {
                    const temp = fromToken;
                    setFromToken(toToken);
                    setToToken(temp);
                    const tempAmount = fromAmount;
                    setFromAmount(toAmount);
                    setToAmount(tempAmount);
                  }}
                  className="p-2 bg-gray-100 dark:bg-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  <ArrowLeftRight className="w-5 h-5" />
                </button>
              </div>

              {/* To Token */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    To
                  </label>
                  {isLoadingBalances ? (
                    <span className="text-xs text-gray-500">Loading...</span>
                  ) : (
                    <span className="text-xs text-gray-500">
                      Balance: {parseFloat(toBalance).toFixed(6)} {toToken.symbol}
                    </span>
                  )}
                </div>
                <div className="flex space-x-2">
                  <select
                    value={toToken.symbol}
                    onChange={(e) => {
                      const token = SUPPORTED_TOKENS.find(t => t.symbol === e.target.value);
                      if (token) setToToken(token);
                    }}
                    className="input-field w-32"
                  >
                    {SUPPORTED_TOKENS.map(token => (
                      <option key={token.symbol} value={token.symbol}>
                        {token.symbol}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    value={toAmount}
                    onChange={(e) => setToAmount(e.target.value)}
                    placeholder="0.0"
                    className="input-field flex-1"
                    step="0.000001"
                    min="0"
                    readOnly
                  />
                </div>
              </div>

              {parseFloat(fromAmount) > parseFloat(fromBalance) && (
                <p className="text-sm text-red-500">
                  Insufficient balance. Available: {parseFloat(fromBalance).toFixed(6)} {fromToken.symbol}
                </p>
              )}
              <button
                onClick={handleSwap}
                disabled={isSwapping || !fromAmount || parseFloat(fromAmount) <= 0 || parseFloat(fromAmount) > parseFloat(fromBalance)}
                className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSwapping ? 'Swapping...' : 'Swap'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Transfer Modal Component
const TransferModalComponent = ({ isOpen, onClose, onTransferSuccess }) => {
  const { isConnected, signer, walletAddress, provider, connectWallet, onCorrectNetwork, networkName, ethBalance } = useWeb3();
  const queryClient = useQueryClient();
  const [selectedToken, setSelectedToken] = useState(DEFAULT_TRANSFER_TOKEN);
  const [recipientAddress, setRecipientAddress] = useState('');
  const [amount, setAmount] = useState('');
  const [isTransferring, setIsTransferring] = useState(false);
  const [tokenBalance, setTokenBalance] = useState('0');
  const [ethPrice, setEthPrice] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setIsTransferring(false);
    }
  }, [isOpen]);

  // Fetch USDC price for conversion - use same method as ChatDetailPage with useQuery
  // Use the same query key as ChatDetailPage to share the cache
  const { data: ethPriceData, isLoading: ethPriceLoading } = useQuery(
    'ethPrice', // Same key as ChatDetailPage to share cache
    async () => {
      try {
        // Use the same endpoint as Escrowintegration.js and ChatDetailPage
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const response = await axios.get(`${API_URL}/api/v1/price/usdc`);
        console.log('USDC price fetched in transfer modal:', response.data);
        return response.data;
      } catch (error) {
        console.error('Failed to get USDC price from backend:', error);
        return null;
      }
    },
    {
      select: (data) => data,
      enabled: isOpen, // Only fetch when modal is open
      staleTime: 300000, // Cache for 5 minutes
      refetchInterval: 300000, // Refetch every 5 minutes
      onSuccess: (data) => {
        if (data?.usd) {
          const price = parseFloat(data.usd);
          if (price && price > 0 && !isNaN(price)) {
            setEthPrice(price);
            console.log('USDC price set in transfer modal:', price);
          }
        }
      }
    }
  );

  // Update ethPrice state when data changes
  useEffect(() => {
    if (ethPriceData?.usd) {
      const price = parseFloat(ethPriceData.usd);
      if (price && price > 0 && !isNaN(price)) {
        setEthPrice(price);
        console.log('USDC price updated in transfer modal:', price);
      } else {
        console.warn('Invalid USDC price received:', ethPriceData.usd);
      }
    } else if (ethPriceData === null) {
      // Data fetch failed, keep existing price or set to null
      console.warn('USDC price fetch returned null');
    }
  }, [ethPriceData]);

  // Fetch token balance when modal opens or token changes
  useEffect(() => {
    const fetchBalance = async () => {
      if (!isConnected || !provider || !walletAddress || !isOpen) {
        setTokenBalance('0');
        return;
      }

      try {
        if (selectedToken.address === null) {
          // Native token (ETH)
          const balance = await provider.getBalance(walletAddress);
          setTokenBalance(formatEther(balance));
        } else {
          const contract = new Contract(selectedToken.address, ERC20_ABI, provider);
          const [balance, decimals] = await Promise.all([
            contract.balanceOf(walletAddress),
            readTokenDecimals(contract, selectedToken.decimals),
          ]);
          setTokenBalance(formatUnits(balance, decimals));
        }
      } catch (error) {
        console.error('Error fetching token balance:', error);
        setTokenBalance('0');
      }
    };

    if (isOpen && isConnected) {
      fetchBalance();
    }
  }, [isOpen, isConnected, provider, walletAddress, selectedToken]);

  // Calculate USD value from token amount (for ETH only)
  const calculateUsdValue = (tokenAmount) => {
    // Only show USD conversion for ETH
    if (selectedToken.symbol !== 'ETH') return null;
    
    if (!tokenAmount) return null;
    const tokenValue = parseFloat(tokenAmount);
    if (isNaN(tokenValue) || tokenValue <= 0) return null;
    
    if (!ethPrice) return null;
    const price = parseFloat(ethPrice);
    if (isNaN(price) || price <= 0) return null;
    
    const usdValue = tokenValue * price;
    if (isNaN(usdValue) || usdValue <= 0) return null;
    
    return usdValue.toFixed(2);
  };

  // Calculate balance in USD (for ETH only)
  const balanceInUSD = useMemo(() => {
    if (!tokenBalance || !ethPrice || selectedToken.symbol !== 'ETH') return null;
    const tokenValue = parseFloat(tokenBalance);
    if (tokenValue <= 0) return '0';
    
    const usdValue = tokenValue * ethPrice;
    return usdValue.toFixed(2);
  }, [tokenBalance, ethPrice, selectedToken.symbol]);

  // Resolve signer function - same pattern as ChatDetailPage
  // In ethers v6, getSigner() returns a Promise, so we always get it fresh from provider
  const resolveSigner = async () => {
    if (!provider) {
      console.error('No provider available');
      return null;
    }

    try {
      // Always get signer from provider (ethers v6 getSigner returns Promise)
      const resolvedSigner = await provider.getSigner();
      
      // Verify it has sendTransaction method
      if (resolvedSigner && typeof resolvedSigner.sendTransaction === 'function') {
        console.log('Successfully resolved signer from provider');
        return resolvedSigner;
      } else {
        console.error('Signer from provider does not have sendTransaction method');
        return null;
      }
    } catch (error) {
      console.error('Unable to resolve signer from provider:', error);
      return null;
    }
  };

  const handleTransfer = async () => {
    if (!isConnected || !provider) {
      toast.error('Please connect your wallet first');
      return;
    }

    if (!onCorrectNetwork) {
      toast.error(`Switch your wallet to ${networkName || BASE_CHAIN_PARAMS.chainName} before transferring.`);
      return;
    }

    // Resolve signer using the same pattern as ChatDetailPage
    const transferSigner = await resolveSigner();
    if (!transferSigner) {
      toast.error('Unable to access wallet signer. Please reconnect your wallet.');
      return;
    }

    if (!recipientAddress || !recipientAddress.match(/^0x[a-fA-F0-9]{40}$/)) {
      toast.error('Please enter a valid recipient address');
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    // Check balance
    const transferAmount = parseFloat(amount);
    const balance = parseFloat(tokenBalance);
    
    if (transferAmount > balance) {
      toast.error(`Insufficient balance. Available: ${balance.toFixed(6)} ${selectedToken.symbol}`);
      return;
    }

    // Check if transferring to self
    if (recipientAddress.toLowerCase() === walletAddress.toLowerCase()) {
      toast.error('Cannot transfer to your own address');
      return;
    }

    setIsTransferring(true);
    try {
      let txHash;
      let blockNumber;
      let gasUsed = null;
      let gasPrice = null;
      let relayed = false;

      if (selectedToken.address === null) {
        toast.success('Submitting transfer…');
        const result = await transferNativeEth({
          signer: transferSigner,
          from: walletAddress,
          to: recipientAddress,
          amountHuman: amount,
          ethBalance,
        });
        txHash = result.txHash;
        blockNumber = result.blockNumber;
        gasUsed = result.gasUsed;
        gasPrice = result.gasPrice;
        toast.success('Transfer successful!');
      } else {
        const contract = new Contract(selectedToken.address, ERC20_ABI, transferSigner);
        const decimals = await readTokenDecimals(contract, selectedToken.decimals);

        toast.success(
          selectedToken.symbol === 'USDC'
            ? 'Sign to authorize transfer (relayer pays gas)…'
            : 'Submitting transfer…'
        );

        const result = await transferToken({
          signer: transferSigner,
          tokenAddress: selectedToken.address,
          tokenSymbol: selectedToken.symbol,
          from: walletAddress,
          to: recipientAddress,
          amountHuman: amount,
          decimals,
          ethBalance,
        });

        txHash = result.txHash;
        blockNumber = result.blockNumber;
        gasUsed = result.gasUsed;
        gasPrice = result.gasPrice;
        relayed = !!result.relayed;
        toast.success(
          relayed ? 'Transfer relayed successfully (no ETH needed)!' : 'Transfer successful!'
        );
      }

      // Save transaction to backend
      try {
        // Prepare transaction data
        const transactionData = {
          txHash,
          amount: transferAmount,
          type: 'transfer',
          tokenSymbol: selectedToken.symbol,
          fromAddress: walletAddress,
          toAddress: recipientAddress,
          blockNumber,
          gasUsed,
          description: `${relayed ? 'Relayed ' : ''}Transfer ${amount} ${selectedToken.symbol} to ${recipientAddress.slice(0, 6)}...${recipientAddress.slice(-4)}`
        };

        // Only include tokenAddress if it's not null (for ERC20 tokens)
        if (selectedToken.address !== null) {
          transactionData.tokenAddress = selectedToken.address;
        }

        // Only include gasPrice if it's available and valid
        if (gasPrice && gasPrice !== '0' && gasPrice !== null) {
          transactionData.gasPrice = gasPrice;
        }

        console.log('Saving transaction to backend:', transactionData);

        await api.post(API_ENDPOINTS.WALLET.TRANSACTIONS_ONCHAIN, transactionData);
        
        // Refresh transactions list
        queryClient.invalidateQueries(['walletTransactions']);
        
        toast.success('Transaction recorded successfully!');
      } catch (error) {
        console.error('Error saving transaction to backend:', error);
        console.error('Error response:', error.response?.data);
        toast.error(error.response?.data?.error || 'Transfer successful but failed to save transaction record. Please contact support.');
      }

      // Refresh balance
      try {
        if (selectedToken.address === null) {
          // For ETH, the balance will update automatically via Web3Context
          const balance = await provider.getBalance(walletAddress);
          setTokenBalance(formatEther(balance));
        } else {
          const contract = new Contract(selectedToken.address, ERC20_ABI, provider);
          const [balance, decimals] = await Promise.all([
            contract.balanceOf(walletAddress),
            readTokenDecimals(contract, selectedToken.decimals),
          ]);
          setTokenBalance(formatUnits(balance, decimals));
        }
      } catch (error) {
        console.error('Error refreshing balance:', error);
      }

      // Call success callback if provided
      if (onTransferSuccess) {
        onTransferSuccess();
      }

      // Reset form
      setRecipientAddress('');
      setAmount('');
      
      onClose();
    } catch (error) {
      console.error('Transfer error:', error);
      toast.error(formatWalletError(error, 'Transfer failed. Please try again.'));
    } finally {
      dismissWalletToasts();
      setIsTransferring(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-heading text-heading dark:text-white">
            Transfer Funds
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {!isConnected ? (
            <div className="text-center py-8">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Please connect your wallet first
              </p>
              <button
                onClick={async () => {
                  const result = await connectWallet();
                  if (result && result.address) {
                    toast.success('Wallet connected!');
                  }
                }}
                className="btn-primary"
              >
                Connect Wallet
              </button>
            </div>
          ) : (
            <>
              {/* Token Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select Token
                </label>
                <select
                  value={selectedToken.symbol}
                  onChange={(e) => {
                    const token = SUPPORTED_TOKENS.find(t => t.symbol === e.target.value);
                    if (token) setSelectedToken(token);
                  }}
                  className="input-field w-full"
                >
                  {SUPPORTED_TOKENS.map(token => (
                    <option key={token.symbol} value={token.symbol}>
                      {token.name} ({token.symbol})
                    </option>
                  ))}
                </select>
                {selectedToken.symbol === 'USDC' && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    USDC transfers are relayed — you only sign; no ETH needed for gas.
                  </p>
                )}
                {selectedToken.symbol !== 'USDC' && selectedToken.symbol !== 'ETH' && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    If you have no ETH for gas, the relayer can top up your wallet before sending.
                  </p>
                )}
              </div>

              {/* Recipient Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Recipient Address
                </label>
                <input
                  type="text"
                  value={recipientAddress}
                  onChange={(e) => setRecipientAddress(e.target.value)}
                  placeholder="0x..."
                  className="input-field w-full font-mono"
                />
              </div>

              {/* Amount */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Amount
                  </label>
                  <span className="text-xs text-gray-500">
                    Balance: {parseFloat(tokenBalance).toFixed(6)} {selectedToken.symbol}
                    {balanceInUSD && ` ($${balanceInUSD})`}
                  </span>
                </div>
                <div className="relative">
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.0"
                    className="input-field w-full pr-16"
                    step="0.000001"
                    min="0"
                    max={tokenBalance}
                  />
                  <button
                    onClick={() => setAmount(tokenBalance)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-accent hover:text-accent/80 font-medium"
                  >
                    MAX
                  </button>
                </div>
                {/* Show USD conversion for ETH only */}
                {amount && parseFloat(amount) > 0 && selectedToken.symbol === 'ETH' && (
                  <>
                    {(() => {
                      const usdValue = calculateUsdValue(amount);
                      if (usdValue) {
                        return (
                          <p className="text-xs text-gray-500 mt-1">
                            ≈ ${usdValue} USD
                          </p>
                        );
                      } else if (ethPriceLoading || ethPrice === null || ethPrice === undefined) {
                        return (
                          <p className="text-xs text-yellow-500 mt-1">
                            Loading price...
                          </p>
                        );
                      }
                      return null;
                    })()}
                  </>
                )}
                {amount && parseFloat(amount) > parseFloat(tokenBalance) && (
                  <p className="text-xs text-red-500 mt-1">
                    Insufficient balance
                  </p>
                )}
              </div>

              <button
                onClick={handleTransfer}
                disabled={
                  isTransferring || 
                  !recipientAddress || 
                  !amount || 
                  parseFloat(amount) <= 0 ||
                  parseFloat(amount) > parseFloat(tokenBalance) ||
                  recipientAddress.toLowerCase() === walletAddress?.toLowerCase()
                }
                className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isTransferring ? 'Transferring...' : 'Transfer'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WalletPage;
